// The AI Clinical Gist — advisory, collapsed by default, always cited.
//
// ── Why it looks the way it does ─────────────────────────────────
//
// Collapsed until asked for: it costs a model call, and a summary that
// appears automatically above the real record trains people to read the
// summary instead of the record.
//
// Visually distinct from every other card: an AI paraphrase and a clinician's
// signed note must never be mistakable for one another at a glance.
//
// Every statement carries its sources, resolved server-side against the
// context pack. A statement the model could not ground is rendered with an
// explicit "not traceable" marker rather than dropped — hiding it would hide
// that the model said something ungrounded, which is exactly what a reviewer
// needs to see.
//
// Alerts are NOT rendered here. They live in AlertStrip, computed
// deterministically, and nothing on this card can change them.
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import type { ClinicalGist, SourceRef } from './types';
import { SourceLink, fmtDateTime } from './ui';

export function ClinicalGistCard({
  gist, isPending, error, onGenerate, onOpen,
}: {
  gist: ClinicalGist | undefined;
  isPending: boolean;
  error: Error | null;
  onGenerate: () => void;
  onOpen?: (r: SourceRef) => void;
}) {
  return (
    <section className="rounded-xl border border-dashed border-info/50 bg-info-soft/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold">Clinical gist</h3>
          <Badge variant="info">AI · advisory</Badge>
        </div>
        <Button variant="ghost" size="sm" onClick={onGenerate} loading={isPending}>
          {gist ? 'Regenerate' : 'Generate summary'}
        </Button>
      </div>

      {!gist && !isPending && !error && (
        <p className="mt-2 text-[12.5px] text-ink-muted">
          An optional AI summary of this admission. It reads the same records shown above and
          cites each one. It never changes the safety alerts, writes a note, or places an order.
        </p>
      )}

      {error && (
        <p className="mt-2 text-[12.5px] text-ink-muted">
          The summary could not be generated. The clinical record above is complete and unaffected.
        </p>
      )}

      {gist && !gist.available && (
        <p className="mt-2 text-[12.5px] text-ink-muted">{gist.reason}</p>
      )}

      {gist && gist.available && (
        <>
          <ul className="mt-3 space-y-1.5">
            {gist.statements.map((s, i) => (
              <li key={i} className="flex flex-wrap items-baseline gap-x-2 text-[13px]">
                <span className="text-ink-muted">•</span>
                <span className="min-w-0 flex-1">{s.text}</span>
                {s.unverified ? (
                  // Surfaced, never silently removed.
                  <Badge variant="warn">not traceable — verify</Badge>
                ) : (
                  <span className="flex gap-1.5">
                    {s.sources.map((src, j) => (
                      <SourceLink key={j} source={src} onOpen={onOpen} label={`↗ ${src.type.replace(/_/g, ' ')}`} />
                    ))}
                  </span>
                )}
              </li>
            ))}
          </ul>
          <div className="mt-3 border-t border-info/30 pt-2 text-[11px] text-ink-muted">
            Generated by {gist.provenance.provider} ({gist.provenance.model}) ·{' '}
            {fmtDateTime(gist.provenance.generated_at)} · Advisory summary, not a clinical record.
            The clinician remains responsible for all clinical decisions.
          </div>
        </>
      )}
    </section>
  );
}
